import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material'
import { PhotoCameraRounded, QrCodeScannerRounded, VideocamOffRounded } from '@mui/icons-material'
import SmartDialog from '../utilities/SmartDialog/SmartDialog'
import useSmartTranslation from '../hooks/useSmartTranslation/useSmartTranslation'
import { parseQrPayload } from './qrPayload'
import useQrInput from './useQrInput'
import QrCameraScanner from './QrCameraScanner'
import QrScanFrame from './QrScanFrame'
import { EYEBROW_SX, codeSx } from './qrReadingStyles'

/**
 * Reusable QR reading dialog. Three input paths, in order of real shop-floor use:
 *   1. Physical reader (keyboard wedge) — types the payload into the focused field and closes with Enter.
 *   2. Camera — opt-in, for terminals/tablets without a reader.
 *   3. Manual entry — last resort; accepts the full MIC payload OR a plain code (e.g. the part code).
 *
 * The dialog only READS: it hands the caller the parsed identity and the caller decides what to do with it.
 *
 * What it looks like follows from that order. The frame at the top is the viewfinder of whoever put this
 * dialog on screen — the same one their widget shows while idle — and it is doing the work in the common
 * case: the operator points a reader at a label and never touches the dialog. Below it, the field that
 * reader types into, set in the monospace the codes are printed in. The camera is one button away
 * because most terminals have a reader, and the two ways out are the quietest things here.
 */

// Entity names travel as ONE uppercase string. Map the known ones to a readable, translatable label;
// an unknown type falls back to itself (t_ echoes missing keys).
const ENTITY_LABELS = {
  PART: 'Part',
  RAWMATERIAL: 'Raw Material',
  FABRICATIONORDER: 'Fabrication Order',
  ORDER: 'Order',
  TOOL: 'Tool',
  FIXTURE: 'Fixture'
}

// A dialog the size of a panel: this is the operator's whole task while it is open, on a screen that
// is wide and short. 94vw is the floor for a portrait tablet, where it becomes one column.
const PAPER_WIDTH = '58rem'
// The square being aimed at. Capped against the viewport height because a plant terminal has little of
// it, and a target taller than the screen is worse than a smaller one.
const TARGET_SIZE = 'min(24rem, 46vh)'

// Accepts both the code-side name ('RawMaterial') and the wire form ('RAWMATERIAL').
const wireEntityType = (entityType) => String(entityType).toUpperCase()
const entityTypeLabel = (entityType, t_) => t_(ENTITY_LABELS[wireEntityType(entityType)] || entityType)

const QrScannerDialog = ({
  open,
  onClose,
  onScan,
  title = 'Scan QR code',
  expectedEntityTypes = [],
  allowManual = true,
  allowCamera = true
}) => {
  const { t_ } = useSmartTranslation()
  const [manualValue, setManualValue] = useState('')
  const [cameraOn, setCameraOn] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  // Keystroke timing, to tell a reader typing into the field apart from a human typing (source reporting).
  const typing = useRef({ startedAt: 0, lastAt: 0, count: 0 })

  // Fresh state on every open: a stale error or a half-typed code from the previous scan would mislead.
  useEffect(() => {
    if (!open) return
    setManualValue('')
    setCameraOn(false)
    setError('')
    setBusy(false)
    typing.current = { startedAt: 0, lastAt: 0, count: 0 }
  }, [open])

  const submit = async (raw, source) => {
    if (busy) return
    const trimmed = String(raw || '').trim()
    if (trimmed === '') return

    const parsed = parseQrPayload(trimmed)

    // "Wrong label scanned" — the critical case on the shop floor. Keep the dialog open, never fire the action.
    if (expectedEntityTypes.length > 0 && !expectedEntityTypes.map(wireEntityType).includes(parsed.entityType)) {
      const expected = expectedEntityTypes.map(type => entityTypeLabel(type, t_)).join(', ')
      setError(t_('The scanned code does not match the expected type') + ': ' + expected)
      setManualValue('')
      typing.current = { startedAt: 0, lastAt: 0, count: 0 }
      return
    }

    setError('')
    setBusy(true)
    try {
      const outcome = await onScan({ ...parsed, source })
      // A non-empty string means the caller could not use the code (not found, wrong state…): show it and stay open.
      if (typeof outcome === 'string' && outcome !== '') {
        setError(outcome)
        setManualValue('')
        typing.current = { startedAt: 0, lastAt: 0, count: 0 }
        setBusy(false)
        return
      }
      setBusy(false)
      onClose()
    } catch (exception) {
      setError(t_('The scanned code could not be processed'))
      setManualValue('')
      setBusy(false)
    }
  }

  // Safety net for a scan that arrives while nothing is focused (e.g. after tapping the camera button).
  // While the manual field has focus the field itself handles the reader — the hook ignores text inputs.
  useQrInput({ enabled: open && !busy, onScan: (raw) => submit(raw, 'scanner') })

  const handleManualChange = (event) => {
    const now = performance.now()
    const state = typing.current
    if (state.count === 0) state.startedAt = now
    state.count += 1
    state.lastAt = now
    setManualValue(event.target.value)
  }

  // A reader delivers the payload either in one burst-paste or at a few ms per key; a person cannot.
  const manualSource = (value) => {
    const { startedAt, lastAt, count } = typing.current
    if (value.length >= 8 && count <= 2) return 'scanner'
    if (count >= 8 && (lastAt - startedAt) < count * 30) return 'scanner'
    return 'manual'
  }

  const submitManual = () => submit(manualValue, manualSource(manualValue.trim()))

  // The shared eyebrow, one step up: this dialog is the size of a panel, a widget card is not.
  const labelSx = { ...EYEBROW_SX, fontSize: '0.72rem' }
  // Buttons a gloved hand can hit, and the same corner radius as the frame and the field.
  const buttonSx = { minHeight: '2.75rem', px: 2, borderRadius: '0.75rem', textTransform: 'none', fontWeight: 700 }

  return (
    <SmartDialog
      setOpen={open}
      close={false}
      accept={false}
      cancel={false}
      title={null}
      closeCallback={onClose}
      paperWidth={'min(' + PAPER_WIDTH + ', 94vw)'}
      renderComponent={
        <Box sx={{ width: '100%', textAlign: 'left', color: 'text.primary' }}>
          {/* The target on one side, everything said about it on the other. A shop-floor screen has
              width to spare and very little height, so a dialog that grows should grow the thing being
              aimed at — not stack more rows the operator has to look down through. On a portrait
              terminal the two swap into one column, title first. */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'stretch' }}>
            {/* TARGET AREA — waiting state, or the camera when the operator asks for it.
                Square, and the same square for both: brackets spread across the full width of a dialog
                this size stop reading as a target and start reading as a bordered empty region, and the
                camera swapping in must not change the shape of what is being aimed at. */}
            <Box
              sx={{
                order: { xs: 2, md: 1 },
                width: { xs: '100%', md: TARGET_SIZE },
                maxWidth: TARGET_SIZE,
                mx: { xs: 'auto', md: 0 },
                flexShrink: 0
              }}
            >
              {cameraOn
                ? <QrCameraScanner onDecode={(text) => submit(text, 'camera')} height={TARGET_SIZE} />
                : (
                  <QrScanFrame height={TARGET_SIZE} gap={1} active={!busy}>
                    {busy
                      ? <CircularProgress size={44} />
                      : <QrCodeScannerRounded sx={{ fontSize: '4.5rem', color: 'primary.main', opacity: 0.9 }} />}
                    <Typography sx={labelSx}>
                      {busy ? t_('Processing') + '…' : t_('Waiting for scan')}
                    </Typography>
                  </QrScanFrame>
                  )}
            </Box>

            {/* What this dialog is, and the two paths that are not the reader. */}
            <Box
              sx={{
                order: { xs: 1, md: 2 },
                flexGrow: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 2.5
              }}
            >
              <Box>
                <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.2 }}>{t_(title)}</Typography>
                {/* Only where there is a camera to use: a station without one is not told about it. */}
                {allowCamera && (
                  <Typography sx={{ fontSize: '1rem', color: 'text.secondary', mt: 0.5 }}>
                    {t_('Scan the label with the reader or the camera')}
                  </Typography>
                )}
              </Box>

              {/* MANUAL ENTRY — also the target the physical reader types into */}
              {allowManual && (
                <Box>
                  <Typography sx={labelSx}>{t_('Code or QR content')}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'stretch' }}>
                    <TextField
                      autoFocus
                      fullWidth
                      disabled={busy}
                      value={manualValue}
                      onChange={handleManualChange}
                      onKeyDown={(event) => {
                        if (event.key !== 'Enter') return
                        event.preventDefault() // a reader ends with Enter; don't let it bubble as a dialog action
                        submitManual()
                      }}
                      inputProps={{ 'aria-label': t_('Code or QR content'), autoComplete: 'off', spellCheck: 'false' }}
                      // The codes are machine-printed and read off a tag: same monospace they come back in.
                      InputProps={{ sx: { ...codeSx('1.15rem'), borderRadius: '0.75rem' } }}
                    />
                    <Button
                      variant='contained'
                      disabled={busy || manualValue.trim() === ''}
                      onClick={submitManual}
                      sx={{ ...buttonSx, flexShrink: 0, px: 3 }}
                    >
                      {t_('Accept')}
                    </Button>
                  </Box>
                </Box>
              )}

              {allowCamera && (
                <Button
                  variant='outlined'
                  startIcon={cameraOn ? <VideocamOffRounded /> : <PhotoCameraRounded />}
                  onClick={() => setCameraOn(prev => !prev)}
                  disabled={busy}
                  sx={{ ...buttonSx, alignSelf: 'flex-start' }}
                >
                  {cameraOn ? t_('Stop camera') : t_('Use camera')}
                </Button>
              )}
            </Box>
          </Box>

          {error !== '' && <Alert severity='error' sx={{ mt: 2.5 }}>{error}</Alert>}

          {/* FOOTER — one job, the way out */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2.5 }}>
            <Button onClick={onClose} sx={{ ...buttonSx, color: 'text.secondary' }}>{t_('Close')}</Button>
          </Box>
        </Box>
      }
    />
  )
}

QrScannerDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  /**
   * Called with { raw, entityType, id, fields, isMicQr, source } — entityType and the field keys come back
   * uppercase (PART, RAWMATERIAL…); source is 'scanner' | 'camera' | 'manual'.
   * May return a promise; the dialog blocks input while it is pending. Resolve to a non-empty string to
   * report a problem: it is shown as an error and the dialog stays open. Anything else closes it.
   */
  onScan: PropTypes.func.isRequired,
  title: PropTypes.string,
  /**
   * Non-empty: only these entity types are accepted, anything else is rejected with an inline error.
   * Either casing works — 'RawMaterial' or 'RAWMATERIAL'.
   */
  expectedEntityTypes: PropTypes.arrayOf(PropTypes.string),
  allowManual: PropTypes.bool,
  allowCamera: PropTypes.bool
}

export default QrScannerDialog
